/**
 * User data store on TotalAccessOfflineDB for caching user/subordinates data offline.
 */
import offlinePayloadCrypto from './offlineCrypto';
import type { OfflineDomainRecord } from './offlineDb';
import offlineDb from './offlineDb';

const DOMAIN = 'app-shell' as const;
const STORE_USER_DATA = 'user_data' as const;

function getDb() {
  return offlineDb;
}

function toEntityId(value: string | number | undefined | null): string {
  return String(value ?? '');
}

async function decryptRecordPayload<T>(record: OfflineDomainRecord | undefined): Promise<T | undefined> {
  if (!record) {
    return undefined;
  }
  return offlinePayloadCrypto.decryptValue<T>(record.payload);
}

async function buildRecordRow<T extends Record<string, unknown>>(
  store: typeof STORE_USER_DATA,
  clientId: number,
  entityId: string,
  item: T,
  existing: OfflineDomainRecord | undefined,
): Promise<OfflineDomainRecord> {
  const now = new Date().toISOString();
  const payload = await offlinePayloadCrypto.encryptValue(item);

  return {
    id: existing?.id,
    domain: DOMAIN,
    store,
    client_id: clientId,
    entity_id: entityId,
    sync_status: 'synced',
    created_at: existing?.created_at || now,
    updated_at: now,
    payload,
    payload_size: payload.length,
  };
}

async function getStoreRow(
  store: typeof STORE_USER_DATA,
  clientId: number,
  entityId: string,
): Promise<OfflineDomainRecord | undefined> {
  const db = getDb();
  if (!db) {
    return undefined;
  }

  return db.records
    .where('[domain+store+client_id+entity_id]')
    .equals([DOMAIN, store, clientId, entityId])
    .first();
}

export interface SubordinateEmployee {
  id: number; // This is user_id
  employee_id?: number | null; // Employee ID for reference
  employee_number: string | null;
  first_name: string | null;
  last_name: string | null;
  preferred_name: string | null;
  email: string | null;
  job_title: string | null;
  department: string | null;
  employment_status: string | null;
  profile_image_url: string | null;
}

export interface UserDataPayload {
  user_id: number;
  employee_id: number | null;
  subordinates: SubordinateEmployee[];
  updated_at: string | Date;
}

const userDataStore = {
  async upsertUserData(
    clientId: number,
    userId: number,
    userData: UserDataPayload,
  ): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const entityId = toEntityId(userId);
    const updatedAt = userData.updated_at;
    const wire: Record<string, unknown> = {
      ...userData,
      updated_at: updatedAt instanceof Date
        ? updatedAt.toISOString()
        : String(updatedAt),
    };

    const existing = await getStoreRow(STORE_USER_DATA, clientId, entityId);
    const row = await buildRecordRow(STORE_USER_DATA, clientId, entityId, wire, existing);
    await db.records.put(row);
  },

  async getUserData(
    clientId: number,
    userId: number,
  ): Promise<UserDataPayload | undefined> {
    const row = await getStoreRow(STORE_USER_DATA, clientId, toEntityId(userId));
    const raw = await decryptRecordPayload<Record<string, unknown>>(row);
    if (!raw || typeof raw !== 'object') {
      return undefined;
    }
    return raw as unknown as UserDataPayload;
  },

  async deleteUserDataForClient(clientId: number): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }
    const rows = await db.records
      .where('[domain+store+client_id]')
      .equals([DOMAIN, STORE_USER_DATA, clientId])
      .toArray();
    const ids = rows.map((r) => r.id).filter((id): id is number => typeof id === 'number');
    if (ids.length > 0) {
      await db.records.bulkDelete(ids);
    }
  },
};

export default userDataStore;
